import ThrottledApi from './api/throttledApi';
import { AxiosResponse } from 'axios';
import {
  CurrentInventoryStatusParams,
  CurrentInventoryStatusResponse,
  LotteryPackSnapshotResponse,
  OverdueBackstockPacksResponse,
  PackInvestigationResponse,
  PackLifecycleResponse,
  GameReconciliationLedgerResponse,
} from 'features/scratchers/types';
import { SCRATCHER_ENDPOINTS } from 'constants/apiEndPoints';

interface GamePackReportResponse {
  success: boolean;
  data: any | null;
  error: string | null;
}

interface PackLocationUpdateResponse {
  success: boolean;
  data: any | null;
  error: string | null;
}

interface PreTrackingSoldOutUpdateResponse {
  success: boolean;
  data: any | null;
  error: string | null;
}

interface ManualSoldOutPack {
  game_id: string | number;
  pack_id: string | number;
}

interface ManualSoldOutBulkResponse {
  success: boolean;
  data: any | null;
  error: string | null;
}

export interface ManualSoldOutReportPack {
  audit_id: string;
  audit_detail_id: string;
  game_id: number;
  game_name?: string | null;
  pack_id: number;
  tickets_per_pack?: number | null;
  tickets: number;
  ticket_value: number | string;
  pack_value: number | string;
  retail_value: number | string;
  marked_at: string;
  marked_date: string;
  affected_at: string;
  affected_date: string;
}

export interface ManualSoldOutReport {
  store_id: string;
  report_date: string;
  timezone: string;
  pack_count: number;
  total_tickets: number;
  total_retail_value: number | string;
  packs: ManualSoldOutReportPack[];
}

export interface ManualSoldOutImpact {
  store_id: string;
  affected_date: string;
  timezone: string;
  pack_count: number;
  total_tickets: number;
  total_retail_value: number | string;
  packs: ManualSoldOutReportPack[];
}

export interface ManualSoldOutEligibilityResult {
  game_id: number;
  pack_id: number;
  can_mark_manual_sold_out: boolean;
  current_subgroup?: string | null;
  current_status?: string | null;
  returned_to_counter_at?: string | null;
  returned_to_counter_audit_id?: string | null;
  message?: string | null;
}

interface ManualSoldOutEligibilityResponse {
  success: boolean;
  data: { results: ManualSoldOutEligibilityResult[] } | null;
  error: string | null;
}

interface CurrentInventoryStatusServiceResponse {
  success: boolean;
  data: CurrentInventoryStatusResponse | null;
  error: string | null;
}

interface LotteryPackSnapshotServiceResponse {
  success: boolean;
  data: LotteryPackSnapshotResponse | null;
  error: string | null;
}

interface BackstockAuditStatusServiceResponse {
  success: boolean;
  data: OverdueBackstockPacksResponse | null;
  error: string | null;
}

interface PackInvestigationServiceResponse {
  success: boolean;
  data: PackInvestigationResponse | null;
  error: string | null;
}

const stringifyApiError = (value: unknown): string => {
  if (typeof value === 'string') {
    return value;
  }
  if (Array.isArray(value)) {
    const parts = value.map((item) => {
      if (typeof item === 'string') {
        return item;
      }
      if (item && typeof item === 'object' && 'msg' in item) {
        return String((item as any).msg);
      }
      return '';
    }).filter(Boolean);
    return parts.join('; ');
  }
  if (value && typeof value === 'object') {
    if ('detail' in (value as Record<string, unknown>)) {
      return stringifyApiError((value as Record<string, unknown>).detail);
    }
    if ('message' in (value as Record<string, unknown>)) {
      return stringifyApiError((value as Record<string, unknown>).message);
    }
  }
  return '';
};

// Game Pack Report Services
export const getGamePackReport = async (storeId: string): Promise<GamePackReportResponse> => {
  try {
    const response = await ThrottledApi.get(SCRATCHER_ENDPOINTS.GET_GAME_PACK_REPORT(storeId)) as AxiosResponse;

    if (response.data) {
      return {
        success: true,
        data: response.data,
        error: null,
      };
    }

    return {
      success: false,
      data: null,
      error: 'No data received from game pack report',
    };
  } catch (error: any) {
    console.error('ScratcherService: Error fetching game pack report', error);
    return {
      success: false,
      data: null,
      error: error?.response?.data || error?.message || 'Failed to fetch game pack report',
    };
  }
};

export const getGamePackDetail = async (storeId: string, gameId: string): Promise<GamePackReportResponse> => {
  try {
    const response = await ThrottledApi.get(SCRATCHER_ENDPOINTS.GET_GAME_PACK_DETAIL(storeId, gameId)) as AxiosResponse;

    if (response.data) {
      return {
        success: true,
        data: response.data,
        error: null,
      };
    }

    return {
      success: false,
      data: null,
      error: 'No data received for game pack detail',
    };
  } catch (error: any) {
    console.error('ScratcherService: Error fetching game pack detail', error);
    return {
      success: false,
      data: null,
      error: error?.response?.data || error?.message || 'Failed to fetch game pack detail',
    };
  }
};

export const getStoreGames = async (storeId: string): Promise<GamePackReportResponse> => {
  try {
    const response = await ThrottledApi.get(SCRATCHER_ENDPOINTS.GET_STORE_GAMES_LIST(storeId)) as AxiosResponse;

    if (response.data) {
      return {
        success: true,
        data: response.data,
        error: null,
      };
    }

    return {
      success: false,
      data: null,
      error: 'No data received for store games',
    };
  } catch (error: any) {
    console.error('ScratcherService: Error fetching store games', error);
    return {
      success: false,
      data: null,
      error: error?.response?.data || error?.message || 'Failed to fetch store games',
    };
  }
};

export const getCurrentInventoryStatus = async (
  storeId: string,
  filters: CurrentInventoryStatusParams = {}
): Promise<CurrentInventoryStatusServiceResponse> => {
  try {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      const normalizedValue = String(value ?? '').trim();
      if (normalizedValue) {
        params.set(key, normalizedValue);
      }
    });

    const queryString = params.toString();
    const endpoint = `${SCRATCHER_ENDPOINTS.GET_CURRENT_INVENTORY_STATUS(storeId)}${queryString ? `?${queryString}` : ''}`;
    const response = await ThrottledApi.get(endpoint) as AxiosResponse<CurrentInventoryStatusResponse>;

    if (response.data) {
      return {
        success: true,
        data: response.data,
        error: null,
      };
    }

    return {
      success: false,
      data: null,
      error: 'No data received for current inventory status',
    };
  } catch (error: any) {
    console.error('ScratcherService: Error fetching current inventory status', error);
    return {
      success: false,
      data: null,
      error: stringifyApiError(error?.response?.data) || error?.message || 'Failed to fetch current inventory status',
    };
  }
};

export const getLotteryPacks = async (
  storeId: string,
  params: Pick<CurrentInventoryStatusParams, 'report_date' | 'include_action_eligibility'> = {}
): Promise<LotteryPackSnapshotServiceResponse> => {
  try {
    const searchParams = new URLSearchParams();
    if (params.report_date) {
      searchParams.set('report_date', params.report_date);
    }
    if (params.include_action_eligibility !== undefined) {
      searchParams.set('include_action_eligibility', String(params.include_action_eligibility));
    }

    const queryString = searchParams.toString();
    const endpoint = `${SCRATCHER_ENDPOINTS.GET_LOTTERY_PACKS(storeId)}${queryString ? `?${queryString}` : ''}`;
    const response = await ThrottledApi.get(endpoint) as AxiosResponse<LotteryPackSnapshotResponse>;

    if (response.data) {
      return {
        success: true,
        data: response.data,
        error: null,
      };
    }

    return {
      success: false,
      data: null,
      error: 'No data received for lottery packs',
    };
  } catch (error: any) {
    console.error('ScratcherService: Error fetching lottery packs', error);
    return {
      success: false,
      data: null,
      error: stringifyApiError(error?.response?.data) || error?.message || 'Failed to fetch lottery packs',
    };
  }
};

export const getBackstockAuditStatus = async (
  storeId: string
): Promise<BackstockAuditStatusServiceResponse> => {
  try {
    const response = await ThrottledApi.get(
      SCRATCHER_ENDPOINTS.GET_BACKSTOCK_AUDIT_STATUS(storeId)
    ) as AxiosResponse<OverdueBackstockPacksResponse>;

    return {
      success: true,
      data: response.data,
      error: null,
    };
  } catch (error: any) {
    console.error('ScratcherService: Error fetching backstock audit status', error);
    return {
      success: false,
      data: null,
      error: stringifyApiError(error?.response?.data) || error?.message || 'Failed to load backstock audit status',
    };
  }
};

export const investigatePack = async (
  storeId: string,
  gameId: string,
  packId: string
): Promise<PackInvestigationServiceResponse> => {
  try {
    const response = await ThrottledApi.post(
      SCRATCHER_ENDPOINTS.INVESTIGATE_PACK(storeId, gameId, packId)
    ) as AxiosResponse<PackInvestigationResponse>;

    if (response.data) {
      return {
        success: true,
        data: response.data,
        error: null,
      };
    }

    return {
      success: false,
      data: null,
      error: 'No investigation data received',
    };
  } catch (error: any) {
    console.error('ScratcherService: Error investigating pack:', error);
    return {
      success: false,
      data: null,
      error: stringifyApiError(error?.response?.data) || error?.message || 'Failed to investigate pack',
    };
  }
};

// Game Services
export const getGameHistory = async (storeId: string, gameId: string, status = 'all') => {
  try {
    const response = await ThrottledApi.get(SCRATCHER_ENDPOINTS.GET_GAME_HISTORY(storeId, gameId, status)) as AxiosResponse;
    if (response.data) {
      return response.data;
    }
    return null;
  } catch (error: any) {
    console.error('ScratcherService: Error getting game history:', error);
    return null;
  }
};

// Pack Services
export const getPackLifecycle = async (storeId: string, gameId: string, packId: string): Promise<PackLifecycleResponse> => {
  try {
    const response = await ThrottledApi.get(SCRATCHER_ENDPOINTS.GET_PACK_LIFECYCLE(storeId, gameId, packId)) as AxiosResponse;
    
    if (response.data) {
      return {
        success: true,
        data: response.data,
        error: null
      };
    }

    return {
      success: false,
      data: null,
      error: 'No data received'
    };
  } catch (error: any) {
    console.error('ScratcherService: Error getting pack lifecycle:', error);
    return {
      success: false,
      data: null,
      error: error.response?.data || error.message
    };
  }
};

export const getGameReconciliationLedger = async (
  storeId: string,
  gameId: string,
  startDate: string,
  endDate: string,
): Promise<GameReconciliationLedgerResponse> => {
  const response = await ThrottledApi.get(
    SCRATCHER_ENDPOINTS.GET_GAME_RECONCILIATION_LEDGER(storeId, gameId),
    { params: { start_date: startDate, end_date: endDate } },
  ) as AxiosResponse<GameReconciliationLedgerResponse>;

  return response.data;
};

export const updatePackLocation = async (
  storeId: string,
  gameId: string,
  packId: string,
  location: 'cashier' | 'locker' | 'vending' | 'missing' | 'missing_reconciled'
): Promise<PackLocationUpdateResponse> => {
  try {
    const requestId = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `pack-location-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

    const payload = { location, request_id: requestId };
    const response = await ThrottledApi.patch(
      SCRATCHER_ENDPOINTS.UPDATE_PACK_LOCATION(storeId, gameId, packId),
      payload
    ) as AxiosResponse;

    return {
      success: true,
      data: response.data,
      error: null,
    };
  } catch (error: any) {
    console.error('ScratcherService: Error updating pack location:', error);
    const extractedError =
      stringifyApiError(error?.response?.data?.detail) ||
      stringifyApiError(error?.response?.data) ||
      stringifyApiError(error?.message) ||
      'Failed to update pack location';
    return {
      success: false,
      data: null,
      error: extractedError,
    };
  }
};

export const updatePreTrackingSoldOut = async (
  storeId: string,
  gameId: string,
  packId: string,
  isSoldOutBeforeInventoryTracking: boolean
): Promise<PreTrackingSoldOutUpdateResponse> => {
  try {
    const response = await ThrottledApi.patch(
      SCRATCHER_ENDPOINTS.UPDATE_PRE_TRACKING_SOLD_OUT(storeId, gameId, packId),
      { is_sold_out_before_inventory_tracking: isSoldOutBeforeInventoryTracking }
    ) as AxiosResponse;

    return {
      success: true,
      data: response.data,
      error: null,
    };
  } catch (error: any) {
    console.error('ScratcherService: Error updating pre-tracking sold-out flag:', error);
    const extractedError =
      stringifyApiError(error?.response?.data?.detail) ||
      stringifyApiError(error?.response?.data) ||
      stringifyApiError(error?.message) ||
      'Failed to update pre-tracking sold-out flag';
    return {
      success: false,
      data: null,
      error: extractedError,
    };
  }
};

export const bulkMarkManualSoldOut = async (
  storeId: string,
  packs: ManualSoldOutPack[]
): Promise<ManualSoldOutBulkResponse> => {
  try {
    const response = await ThrottledApi.post(
      SCRATCHER_ENDPOINTS.BULK_MANUAL_SOLD_OUT(storeId),
      { packs }
    ) as AxiosResponse;

    return {
      success: true,
      data: response.data,
      error: null,
    };
  } catch (error: any) {
    console.error('ScratcherService: Error bulk marking manual sold out:', error);
    const extractedError =
      stringifyApiError(error?.response?.data?.detail) ||
      stringifyApiError(error?.response?.data) ||
      stringifyApiError(error?.message) ||
      'Failed to mark selected packs manual sold out';
    return {
      success: false,
      data: null,
      error: extractedError,
    };
  }
};

export const getManualSoldOutEligibility = async (
  storeId: string,
  packs: ManualSoldOutPack[]
): Promise<ManualSoldOutEligibilityResponse> => {
  try {
    const response = await ThrottledApi.post(
      SCRATCHER_ENDPOINTS.MANUAL_SOLD_OUT_ELIGIBILITY(storeId),
      { packs }
    ) as AxiosResponse;

    return {
      success: true,
      data: response.data,
      error: null,
    };
  } catch (error: any) {
    console.error('ScratcherService: Error loading manual sold out eligibility:', error);
    const extractedError =
      stringifyApiError(error?.response?.data?.detail) ||
      stringifyApiError(error?.response?.data) ||
      stringifyApiError(error?.message) ||
      'Failed to load pack sold-out eligibility';
    return {
      success: false,
      data: null,
      error: extractedError,
    };
  }
};

export const getManualSoldOutReport = async (
  storeId: string,
  reportDate: string
): Promise<ManualSoldOutReport> => {
  const response = await ThrottledApi.get(
    SCRATCHER_ENDPOINTS.GET_MANUAL_SOLD_OUT_REPORT(storeId),
    { params: { report_date: reportDate } }
  ) as AxiosResponse<ManualSoldOutReport>;

  return response.data;
};

export const getManualSoldOutImpacts = async (
  storeId: string,
  affectedDate: string
): Promise<ManualSoldOutImpact> => {
  const response = await ThrottledApi.get(
    SCRATCHER_ENDPOINTS.GET_MANUAL_SOLD_OUT_IMPACTS(storeId),
    { params: { affected_date: affectedDate } }
  ) as AxiosResponse<ManualSoldOutImpact>;

  return response.data;
};

export const bulkUnmarkManualSoldOut = async (
  storeId: string,
  packs: ManualSoldOutPack[]
): Promise<ManualSoldOutBulkResponse> => {
  try {
    const response = await ThrottledApi.delete(
      SCRATCHER_ENDPOINTS.BULK_MANUAL_SOLD_OUT(storeId),
      { data: { packs } }
    ) as AxiosResponse;

    return {
      success: true,
      data: response.data,
      error: null,
    };
  } catch (error: any) {
    console.error('ScratcherService: Error bulk unmarking manual sold out:', error);
    const extractedError =
      stringifyApiError(error?.response?.data?.detail) ||
      stringifyApiError(error?.response?.data) ||
      stringifyApiError(error?.message) ||
      'Failed to unmark selected manual sold-out packs';
    return {
      success: false,
      data: null,
      error: extractedError,
    };
  }
};

// Export all functions as a single object for convenience
const scratcherService = {
  getGamePackReport,
  getGamePackDetail,
  getStoreGames,
  getCurrentInventoryStatus,
  getLotteryPacks,
  getBackstockAuditStatus,
  investigatePack,
  getGameHistory,
  getPackLifecycle,
  getGameReconciliationLedger,
  updatePackLocation,
  updatePreTrackingSoldOut,
  getManualSoldOutReport,
  getManualSoldOutImpacts,
  getManualSoldOutEligibility,
  bulkMarkManualSoldOut,
  bulkUnmarkManualSoldOut,
};

export default scratcherService;
